import { db } from "../firebase/firebaseConfig";
import {
  doc,
  updateDoc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  collection,
  writeBatch,
  runTransaction,
  Timestamp,
  DocumentSnapshot,
  limit,
  DocumentReference,
  startAfter,
  QueryConstraint,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import {
  Collections,
  Artist,
  Song,
  SongRequest,
  Status,
  User,
} from "../types/type";
import {
  generateIdByNowDate,
  generateRandomString,
} from "../module/common/string.util";

// 통합된 업데이트 함수
export const updateSongRequestToComplete = async ({
  songRequestId,
  userId,
  newSongId,
}: {
  songRequestId: string;
  userId: string;
  newSongId: string;
}): Promise<void> => {
  const songRequestRef = doc(db, Collections.SONG_REQUEST, songRequestId);
  const userRef = doc(db, Collections.USER, userId);

  try {
    await runTransaction(db, async (transaction) => {
      // 1. Get user document
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error(`User document with ID ${userId} does not exist`);
      }

      const userData = userDoc.data() as User;

      // 2. Update song request status
      transaction.update(songRequestRef, {
        status: Status.COMPLETE,
        songId: newSongId,
      });

      // 3. Update user's song request list
      const updatedSongRequestList = userData.songRequestList.map((request) => {
        if (request.songRequestId === songRequestId) {
          return { ...request, status: Status.COMPLETE, songId: newSongId };
        }
        return request;
      });

      // 4. Update credit balance if applicable
      const updatedCredit = userData.credit
        ? {
            ...userData.credit,
            balance:
              userData.credit.balance > 0
                ? userData.credit.balance - 1
                : userData.credit.balance,
          }
        : undefined;

      // userNotification
      const newNotification = {
        id: generateIdByNowDate() + generateRandomString(6),
        songId: newSongId,
        type: "NEW_SONG" as const,
        isRead: false,
        createdAt: Timestamp.now(),
      };

      // 5. Apply all updates in a single transaction
      transaction.update(userRef, {
        songRequestList: updatedSongRequestList,
        ...(updatedCredit && { credit: updatedCredit }),
        notificationList: arrayUnion(newNotification),
      });
    });

    console.log(
      `Successfully updated song request and user credit for user ${userId}`
    );
  } catch (error) {
    console.error("Error in updateSongRequestWithCredit: ", error);
    throw error;
  }
};

export const updateSongRequestToReady = async ({
  songRequestId,
  userId,
}: {
  songRequestId: string;
  userId: string;
}): Promise<void> => {
  const songRequestRef = doc(db, Collections.SONG_REQUEST, songRequestId);
  const userRef = doc(db, Collections.USER, userId);

  try {
    await runTransaction(db, async (transaction) => {
      // 1. Get user document
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error(`User document with ID ${userId} does not exist`);
      }

      const userData = userDoc.data() as User;

      // 2. Update song request status
      transaction.update(songRequestRef, {
        status: Status.READY,
      });

      // 3. Update user's song request list
      const updatedSongRequestList = userData.songRequestList.map((request) => {
        if (request.songRequestId === songRequestId) {
          return { ...request, status: Status.READY };
        }
        return request;
      });

      // 5. Apply all updates in a single transaction
      transaction.update(userRef, {
        songRequestList: updatedSongRequestList,
      });
    });

    console.log(
      `Successfully updated song request and user credit for user ${userId}`
    );
  } catch (error) {
    console.error("Error in updateSongRequestWithCredit: ", error);
    throw error;
  }
};

export const updateSongRequestToFailOfBrewingTransaction = async ({
  songRequestId,
  userId,
  brewingTransactionId,
}: {
  songRequestId: string;
  userId: string;
  brewingTransactionId: string;
}): Promise<void> => {
  const brewingTransactionRef = doc(
    db,
    Collections.BREWING_TRANSACTION,
    brewingTransactionId
  );
  const songRequestRef = doc(db, Collections.SONG_REQUEST, songRequestId);
  const userRef = doc(db, Collections.USER, userId);

  try {
    await runTransaction(db, async (transaction) => {
      // 1. Get user document
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error(`User document with ID ${userId} does not exist`);
      }

      const userData = userDoc.data() as User;

      // brewingTransaction 업데이트
      transaction.update(brewingTransactionRef, {
        status: "failed",
        updatedAt: serverTimestamp(),
      });

      // 2. Update song request status
      transaction.update(songRequestRef, {
        status: Status.FAILED,
      });

      // 3. Update user's song request list
      const updatedSongRequestList = userData.songRequestList.map((request) => {
        if (request.songRequestId === songRequestId) {
          return { ...request, status: Status.FAILED };
        }
        return request;
      });

      // userNotification
      const newNotification = {
        id: generateIdByNowDate() + generateRandomString(6),
        songId: "",
        type: "SONG_REQUEST_FAILED" as const,
        isRead: false,
        createdAt: Timestamp.now(),
      };

      // 5. Apply all updates in a single transaction
      transaction.update(userRef, {
        songRequestList: updatedSongRequestList,
        // notificationList: arrayUnion(newNotification),
      });
    });

    console.log(
      `Successfully updated song request and user credit for user ${userId}`
    );
  } catch (error) {
    console.error("Error in updateSongRequestWithCredit: ", error);
    throw error;
  }
};

export const updateSongRequestStatusToComplete = async (
  songRequestId: string,
  userId: string
): Promise<void> => {
  try {
    console.log(`Starting to update songRequest with ID: ${songRequestId}`);
    const songRequestRef = doc(db, Collections.SONG_REQUEST, songRequestId);
    const userRef = doc(db, Collections.USER, userId);

    await updateDoc(songRequestRef, { status: Status.COMPLETE });
    console.log(
      `Updated songRequest status to COMPLETE for ID: ${songRequestId}`
    );

    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const updatedSongRequestList = userData.songRequestList.map(
        (request: any) => {
          if (request.songRequestId === songRequestId) {
            return { ...request, status: Status.COMPLETE };
          }
          return request;
        }
      );

      await updateDoc(userRef, { songRequestList: updatedSongRequestList });
      console.log(`Updated user songRequestList for user ID: ${userId}`);
    } else {
      console.error(`User document with ID ${userId} does not exist`);
    }
  } catch (error) {
    console.error("Error updating song request status: ", error);
  }
};

export const updateSongRequestStatus = async (
  songRequestId: string,
  userId: string,
  newStatus: Status
): Promise<void> => {
  try {
    const songRequestRef = doc(db, Collections.SONG_REQUEST, songRequestId);
    const userRef = doc(db, Collections.USER, userId);

    await runTransaction(db, async (transaction) => {
      // songRequestDoc의 상태 업데이트

      // userDoc의 상태 업데이트
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error(`User document with ID ${userId} does not exist`);
      }

      const userData = userDoc.data();
      const updatedSongRequestList = userData.songRequestList.map(
        (request: any) => {
          if (request.songRequestId === songRequestId) {
            return { ...request, status: newStatus };
          }
          return request;
        }
      );

      transaction.update(songRequestRef, { status: newStatus });
      transaction.update(userRef, { songRequestList: updatedSongRequestList });
    });

    console.log(
      `Successfully updated status to ${newStatus} for ID: ${songRequestId}`
    );
  } catch (error) {
    console.error("Error updating song request status: ", error);
    throw new Error(
      "Failed to update song request status in both songRequestDoc and userDoc."
    );
  }
};

// 모든 데이터 일괄 수정
export const updateAllSongRequests = async (): Promise<void> => {
  try {
    const songRequestCollection = collection(db, Collections.SONG_REQUEST);
    const songRequestsSnapshot = await getDocs(songRequestCollection);

    for (const songRequestDoc of songRequestsSnapshot.docs) {
      await updateDoc(songRequestDoc.ref, { songId: "" });
    }

    console.log("Successfully added songId field to all song requests.");
  } catch (error) {
    console.error("Error updating all song requests: ", error);
  }
};

export const updateAllSongGenreField = async () => {
  const songsCollection = collection(db, Collections.SONG);
  const snapshot = await getDocs(songsCollection);
  const batch = writeBatch(db);

  snapshot.docs.forEach((doc) => {
    batch.update(doc.ref, {
      tag: { genreList: [], ...doc.data().tag },
    });
  });

  await batch.commit();
  console.log("Genre field added to all song documents.");
};

export const updateAllDeviceTagField = async () => {
  const deviceCollection = collection(db, Collections.DEVICE);
  const snapshot = await getDocs(deviceCollection);
  const batch = writeBatch(db);

  snapshot.docs.forEach((doc) => {
    batch.update(doc.ref, { tag: { genreList: [], dislikeSongIdList: [] } });
  });

  await batch.commit();
  console.log("Genre field added to all Device documents.");
};

export const updateAllUserTagField = async () => {
  const userCollection = collection(db, Collections.USER);
  const snapshot = await getDocs(userCollection);
  const batch = writeBatch(db);

  snapshot.docs.forEach((doc) => {
    batch.update(doc.ref, { tag: { genreList: [], dislikeSongIdList: [] } });
  });

  await batch.commit();
  console.log("Genre field added to all User documents.");
};

export const updateSongGenre = async (song: Song) => {
  const songRef = doc(db, Collections.SONG, song.songId);
  await updateDoc(songRef, {
    tag: song.tag,
  });
};

export interface GenresToAdd {
  userId: string;
  genres: string[];
}

export const updateDeviceTagList = async (genresToAdd: GenresToAdd) => {
  const hashedDeviceId = genresToAdd.userId;
  const genreListToAdd = [...genresToAdd.genres];

  const deviceQuery = query(
    collection(db, Collections.DEVICE),
    where("hashedDeviceId", "==", hashedDeviceId)
  );

  const deviceQuerySnapshot = await getDocs(deviceQuery);

  if (!deviceQuerySnapshot.empty) {
    const deviceDoc = deviceQuerySnapshot.docs[0];
    const deviceId = deviceDoc.id;
    const deviceData = deviceDoc.data();
    const currentGenreList = deviceData?.tag?.genreList || [];
    const newGenreList = Array.from(
      new Set([...currentGenreList, ...genreListToAdd])
    );

    console.log(
      `Updating device ${deviceId} with new genre list:`,
      newGenreList
    );

    await setDoc(
      deviceDoc.ref,
      { tag: { genreList: newGenreList } },
      { merge: true }
    );
    console.log("Updated user and device genre lists in Firestore.");
  } else {
    console.log(`No device found for hashed device ID: ${hashedDeviceId}`);
  }
};

interface UpdateDocumentParams {
  documentId: "Song" | "Short" | "Artist";
  field: "allUpdateDate" | "updateDate"; // Ensures that only valid fields of the Update type can be used
}

export const updateUpdateDate = async ({
  documentId,
  field,
}: UpdateDocumentParams) => {
  const docRef = doc(db, "Update", documentId);
  const currentTimestamp = Timestamp.now();

  await updateDoc(docRef, {
    [field]: currentTimestamp,
  });
};

/// songRequest 컬렉션 동기화

export const user컬렉션songRequestList필드songRequest컬렉션동기화 = async ({
  totalUserList,
  totalSongRequestList,
}: {
  totalUserList: User[];
  totalSongRequestList: SongRequest[];
}) => {
  console.log("Starting synchronization process...");

  try {
    // Log the number of users and song requests
    console.log(
      `totalUserList : ${totalUserList.length}, totalSongRequestList : ${totalSongRequestList.length}`
    );

    // Iterate over each user and update their SongRequestList
    for (const user of totalUserList) {
      const userDocRef = doc(db, Collections.USER, user.userId);

      // Filter the song requests that match the user's request list
      const updatedSongRequestList = totalSongRequestList.filter((request) =>
        user.songRequestList.some(
          (userRequest) => userRequest.songRequestId === request.songRequestId
        )
      );

      console.log(updatedSongRequestList.map((request) => request.songId));

      if (updatedSongRequestList.length > 0)
        // Log the number of song requests being updated for the user
        console.log(`${updatedSongRequestList.length}개 song request 업데이트`);

      await updateDoc(userDocRef, {
        songRequestList: updatedSongRequestList,
      });

      // Log success for each user update
      console.log(
        `Successfully updated songRequestList for user: ${user.userId}`
      );
    }

    // Log completion of the synchronization process
    console.log("Synchronization process completed successfully.");
  } catch (error) {
    // Log any errors that occur during the process
    console.error("Error during synchronization process:", error);
  }
};

export const updateMakerUserId = async (
  songId: string,
  userName: string
): Promise<void> => {
  try {
    const songRef = doc(db, Collections.SONG, songId);
    const songDoc = await getDoc(songRef);

    if (!songDoc.exists()) {
      console.error(`Song with ID ${songId} does not exist`);
      return;
    }

    const songData = songDoc.data() as Song;

    // Check if the song's makerUserId needs to be updated
    if (songData.makerUserId !== userName) {
      await updateDoc(songRef, { makerUserId: userName });
      console.log(`Updated makerUserId for song ID: ${songId} to ${userName}`);
    } else {
      console.log(
        `No update needed. Current makerUserId is already ${userName}`
      );
    }
  } catch (error) {
    console.error("Error updating makerUserId: ", error);
  }
};

type CollectionName = "Artist" | "Song" | "Short"; // 세 개의 컬렉션 이름 타입 정의
export type DateField = "updateDate" | "allUpdateDate"; // 업데이트할 필드 타입 정의

export const update업데이트 = async (
  collectionName: CollectionName,
  dateField: DateField
): Promise<void> => {
  try {
    const docRef = doc(db, "Update", collectionName);
    await updateDoc(docRef, {
      [dateField]: Timestamp.now(), // 선택된 필드에 현재 시간으로 업데이트
    });
    console.log(
      `${collectionName}의 ${dateField}가 성공적으로 업데이트되었습니다.`
    );
  } catch (error) {
    console.error(`Error updating ${dateField} for ${collectionName} `, error);
    throw error;
  }
};

export const update유저정보 = async ({
  userId,
  newUserName,
}: {
  userId: string;
  newUserName?: string;
}): Promise<void> => {
  try {
    const updatedData: Partial<User> = {};
    if (newUserName) updatedData.userName = newUserName;

    if (Object.keys(updatedData).length === 0) {
      console.log("No new nickname or email provided for update.");
      return;
    }

    // Start a Firestore transaction
    await runTransaction(db, async (transaction) => {
      // Step 1: Update User Collection
      const userDocRef = doc(db, Collections.USER, userId);
      transaction.update(userDocRef, updatedData);
      console.log(`User profile updated in ${Collections.USER} collection.`);

      // Step 2: Update SongRequest Collection
      const songRequestQuery = query(
        collection(db, Collections.SONG_REQUEST),
        where("userId", "==", userId)
      );
      const songRequestSnapshot = await getDocs(songRequestQuery);

      songRequestSnapshot.forEach((docSnapshot) => {
        const songRequestData = docSnapshot.data() as SongRequest;

        if (
          songRequestData.status === Status.COMPLETE &&
          songRequestData.songId
        ) {
          const songDocRef = doc(db, Collections.SONG, songRequestData.songId);

          // Validate songId path
          if (
            songRequestData.songId &&
            songDocRef.path.split("/").length % 2 === 0
          ) {
            transaction.update(songDocRef, { makerUserId: newUserName });
            console.log(
              `makerUserId updated for songId: ${songRequestData.songId} in ${Collections.SONG} collection.`
            );
          } else {
            console.warn(
              `Invalid songId reference for songId: ${songRequestData.songId}`
            );
          }
        }

        // Update userName in SongRequest if necessary
        transaction.update(docSnapshot.ref, updatedData);
        console.log(
          `User profile updated in ${Collections.SONG_REQUEST} collection for song request ID: ${docSnapshot.id}`
        );
      });

      // Step 3: Update Device Collection
      const deviceQuery = query(
        collection(db, Collections.DEVICE),
        where("userId", "==", userId)
      );
      const deviceSnapshot = await getDocs(deviceQuery);
      deviceSnapshot.forEach((docSnapshot) => {
        transaction.update(docSnapshot.ref, updatedData);
        console.log(
          `User profile updated in ${Collections.DEVICE} collection for device ID: ${docSnapshot.id}`
        );
      });
    });

    console.log(
      "All relevant collections updated successfully within a transaction."
    );
  } catch (error) {
    console.error(
      "Error updating user profile across all collections within a transaction:",
      error
    );
  }
};

export const updateArtist = async (
  artistId: string,
  updateData: Partial<Artist>
): Promise<boolean> => {
  try {
    await runTransaction(db, async (transaction) => {
      const artistDocRef = doc(db, Collections.ARTIST, artistId);

      // 업데이트할 필드만 포함하는 객체 생성
      const filteredData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );

      if (Object.keys(filteredData).length === 0) {
        console.log("No data provided for update.");
        return;
      }

      transaction.update(artistDocRef, filteredData);

      console.log(`Artist updated in ${Collections.ARTIST} collection.`);
    });

    console.log("Artist updated successfully.");
    return true;
  } catch (error) {
    console.error("Error updating artist:", error);

    throw error;
  }
};

type BatchProcessResult = {
  lastDoc: DocumentSnapshot | null;
  isEmpty: boolean;
  size: number;
};

export const update모든아티스트정보동기화 = async (
  artistList: Artist[]
): Promise<void> => {
  const BATCH_SIZE = 100;
  const QUERY_LIMIT = 50;
  const MAX_RETRIES = 3;
  let totalUpdated = 0;
  let startTime = Date.now();

  console.log(`[Sync Start] Processing ${artistList.length} artists...`);

  // Artist 맵 생성 (빠른 조회를 위해)
  const artistMap = new Map(
    artistList.map((artist) => [
      artist.artistId,
      { isMale: artist.isMale, modelName: artist.modelName },
    ])
  );

  const processDocumentWithTransaction = async (
    docRef: DocumentReference,
    updateFn: (data: any) => any
  ): Promise<boolean> => {
    let retries = 0;
    while (retries < MAX_RETRIES) {
      try {
        await runTransaction(db, async (transaction) => {
          const doc = await transaction.get(docRef);
          if (!doc.exists()) return;

          const newData = updateFn(doc.data());
          if (newData) {
            transaction.update(docRef, newData);
          }
        });
        return true;
      } catch (error) {
        retries++;
        if (retries === MAX_RETRIES) {
          console.error(
            `[Error] Failed to update ${docRef.path} after ${MAX_RETRIES} attempts`
          );
          return false;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, Math.random() * 1000)
        );
      }
    }
    return false;
  };

  const updateDocument = (data: any): any | null => {
    let needsUpdate = false;
    const updates: any = {};

    if (data.existingArtist?.artistId) {
      const artistInfo = artistMap.get(data.existingArtist.artistId);
      if (artistInfo) {
        updates.existingArtist = {
          ...data.existingArtist,
          ...artistInfo,
        };
        needsUpdate = true;
      }
    }

    if (data.followArtistList?.length > 0) {
      const updatedList = data.followArtistList.map((artist: any) => {
        const artistInfo = artistMap.get(artist.artistId);
        return artistInfo ? { ...artist, ...artistInfo } : artist;
      });
      if (
        JSON.stringify(data.followArtistList) !== JSON.stringify(updatedList)
      ) {
        updates.followArtistList = updatedList;
        needsUpdate = true;
      }
    }

    if (data.songRequestList?.length > 0) {
      const updatedRequests = data.songRequestList.map((request: any) => {
        if (request.existingArtist?.artistId) {
          const artistInfo = artistMap.get(request.existingArtist.artistId);
          return artistInfo
            ? {
                ...request,
                existingArtist: { ...request.existingArtist, ...artistInfo },
              }
            : request;
        }
        return request;
      });
      if (
        JSON.stringify(data.songRequestList) !== JSON.stringify(updatedRequests)
      ) {
        updates.songRequestList = updatedRequests;
        needsUpdate = true;
      }
    }

    return needsUpdate ? updates : null;
  };

  const processCollection = async (
    collectionName: string,
    whereClause?: QueryConstraint
  ) => {
    let lastDoc = null;
    let processedCount = 0;
    let batchCount = 0;
    const collectionStartTime = Date.now();

    console.log(`[Collection Start] Processing ${collectionName}...`);

    try {
      do {
        batchCount++;
        let q = whereClause
          ? query(
              collection(db, collectionName),
              whereClause,
              limit(QUERY_LIMIT)
            )
          : query(collection(db, collectionName), limit(QUERY_LIMIT));

        if (lastDoc) {
          q = query(q, startAfter(lastDoc));
        }

        const snapshot = await getDocs(q);
        if (snapshot.empty) break;

        const updatePromises = snapshot.docs.map(async (doc) => {
          const updates = updateDocument(doc.data());
          if (updates) {
            const success = await processDocumentWithTransaction(
              doc.ref,
              () => updates
            );
            if (success) processedCount++;
          }
        });

        await Promise.all(
          updatePromises.map((p) =>
            p.catch((e) => console.error(`[Error] Document update failed:`, e))
          )
        );
        lastDoc = snapshot.docs[snapshot.docs.length - 1];

        if (batchCount % 5 === 0) {
          console.log(
            `[Progress] ${collectionName}: ${processedCount} documents updated (${Math.floor(
              (Date.now() - collectionStartTime) / 1000
            )}s)`
          );
        }

        totalUpdated += processedCount;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } while (lastDoc);

      const duration = Math.floor((Date.now() - collectionStartTime) / 1000);
      console.log(
        `[Collection Complete] ${collectionName}: ${processedCount} documents updated in ${duration}s`
      );
    } catch (error) {
      console.error(`[Error] Failed processing ${collectionName}:`, error);
      throw error;
    }
  };

  try {
    await processCollection(
      Collections.SONG_REQUEST,
      where("existingArtist", "!=", null)
    );
    await processCollection(
      Collections.MAKING_REQUEST,
      where("existingArtist", "!=", null)
    );
    await processCollection(Collections.USER);

    const totalDuration = Math.floor((Date.now() - startTime) / 1000);
    console.log(
      `[Sync Complete] Updated ${totalUpdated} documents in ${totalDuration}s`
    );
  } catch (error) {
    console.error("[Error] Sync process failed:", error);
    throw error;
  }

  // try {
  //   // 배치 처리 함수
  //   const processBatch = async (
  //     collectionName: string,
  //     lastDoc: DocumentSnapshot | null = null,
  //     whereClause?: any
  //   ): Promise<BatchProcessResult> => {
  //     let batch = writeBatch(db);
  //     let count = 0;

  //     // 쿼리 구성
  //     let q = whereClause
  //       ? query(collection(db, collectionName), whereClause, limit(QUERY_LIMIT))
  //       : query(collection(db, collectionName), limit(QUERY_LIMIT));

  //     if (lastDoc) {
  //       q = whereClause
  //         ? query(
  //             collection(db, collectionName),
  //             whereClause,
  //             limit(QUERY_LIMIT)
  //           )
  //         : query(collection(db, collectionName), limit(QUERY_LIMIT));
  //     }

  //     const snapshot = await getDocs(q);

  //     for (const doc of snapshot.docs) {
  //       const data = doc.data();
  //       let needsUpdate = false;
  //       const updates: any = {};

  //       // SongRequest & MakingRequest 처리
  //       if (data.existingArtist?.artistId) {
  //         const artistInfo = artistMap.get(data.existingArtist.artistId);
  //         if (artistInfo) {
  //           updates.existingArtist = {
  //             ...data.existingArtist,
  //             ...artistInfo,
  //           };
  //           needsUpdate = true;
  //         }
  //       }

  //       // User 컬렉션 특수 처리
  //       if (collectionName === Collections.USER) {
  //         // followArtistList 처리
  //         if (data.followArtistList?.length > 0) {
  //           const updatedList = data.followArtistList.map((artist: any) => {
  //             const artistInfo = artistMap.get(artist.artistId);
  //             return artistInfo ? { ...artist, ...artistInfo } : artist;
  //           });
  //           if (
  //             JSON.stringify(data.followArtistList) !==
  //             JSON.stringify(updatedList)
  //           ) {
  //             updates.followArtistList = updatedList;
  //             needsUpdate = true;
  //           }
  //         }

  //         // songRequestList 처리
  //         if (data.songRequestList?.length > 0) {
  //           const updatedRequests = data.songRequestList.map((request: any) => {
  //             if (request.existingArtist?.artistId) {
  //               const artistInfo = artistMap.get(
  //                 request.existingArtist.artistId
  //               );
  //               return artistInfo
  //                 ? {
  //                     ...request,
  //                     existingArtist: {
  //                       ...request.existingArtist,
  //                       ...artistInfo,
  //                     },
  //                   }
  //                 : request;
  //             }
  //             return request;
  //           });
  //           if (
  //             JSON.stringify(data.songRequestList) !==
  //             JSON.stringify(updatedRequests)
  //           ) {
  //             updates.songRequestList = updatedRequests;
  //             needsUpdate = true;
  //           }
  //         }
  //       }

  //       if (needsUpdate) {
  //         batch.update(doc.ref, updates);
  //         count++;

  //         if (count >= BATCH_SIZE) {
  //           await batch.commit();
  //           console.log(
  //             `Committed batch of ${count} updates for ${collectionName}`
  //           );
  //           totalUpdated += count;
  //           count = 0;
  //           batch = writeBatch(db);
  //         }
  //       }
  //     }

  //     // 남은 배치 처리
  //     if (count > 0) {
  //       await batch.commit();
  //       console.log(
  //         `Committed final batch of ${count} updates for ${collectionName}`
  //       );
  //       totalUpdated += count;
  //     }

  //     return {
  //       lastDoc: snapshot.docs[snapshot.docs.length - 1],
  //       isEmpty: snapshot.empty,
  //       size: snapshot.size,
  //     };
  //   };

  //   // 각 컬렉션 처리
  //   const processCollection = async (
  //     collectionName: string,
  //     whereClause?: any
  //   ) => {
  //     let lastDoc = null;
  //     let continueFetching = true;

  //     while (continueFetching) {
  //       const result: BatchProcessResult = await processBatch(
  //         collectionName,
  //         lastDoc,
  //         whereClause
  //       );
  //       lastDoc = result.lastDoc;
  //       continueFetching = !result.isEmpty && result.size >= QUERY_LIMIT;
  //     }
  //   };

  //   // SongRequest 처리
  //   console.log("Processing SongRequest collection...");
  //   await processCollection(
  //     Collections.SONG_REQUEST,
  //     where("existingArtist", "!=", null)
  //   );

  //   // MakingRequest 처리
  //   console.log("Processing MakingRequest collection...");
  //   await processCollection(
  //     Collections.MAKING_REQUEST,
  //     where("existingArtist", "!=", null)
  //   );

  //   // User 처리
  //   console.log("Processing User collection...");
  //   await processCollection(Collections.USER);

  //   console.log(`Successfully updated ${totalUpdated} documents`);
  // } catch (error) {
  //   console.error("Error in artist sync process:", error);
  //   throw error;
  // }
};

export const update아티스트네임 = async (): Promise<void> => {
  const BATCH_SIZE = 500;
  let totalProcessed = 0;
  let totalUpdated = 0;
  let batch = writeBatch(db);
  let batchCount = 0;

  try {
    // Artist 컬렉션의 모든 문서 가져오기
    const artistsSnapshot = await getDocs(collection(db, Collections.ARTIST));
    const totalDocs = artistsSnapshot.size;

    console.log(`Starting migration for ${totalDocs} artists`);

    for (const artistDoc of artistsSnapshot.docs) {
      totalProcessed++;
      const artistData = artistDoc.data() as Artist;
      let needsUpdate = false;

      if (
        artistData.modelName === undefined ||
        artistData.modelName === null ||
        artistData.modelName === ""
      ) {
        needsUpdate = true;
      }

      if (needsUpdate) {
        // 기존 데이터를 유지하면서 modelName만 null로 설정
        batch.update(doc(db, Collections.ARTIST, artistDoc.id), {
          modelName: null,
        });

        batchCount++;
        totalUpdated++;

        // BATCH_SIZE에 도달하면 커밋
        if (batchCount >= BATCH_SIZE) {
          await batch.commit();
          console.log(
            `Committed batch: ${totalUpdated}/${totalProcessed} documents processed`
          );
          batch = writeBatch(db);
          batchCount = 0;
        }
      }
    }

    // 남은 배치 처리
    if (batchCount > 0) {
      await batch.commit();
      console.log(
        `Committed final batch: ${totalUpdated}/${totalProcessed} documents processed`
      );
    }

    console.log(`Migration completed successfully:`);
    console.log(`- Total documents processed: ${totalProcessed}`);
    console.log(`- Total documents updated: ${totalUpdated}`);
    console.log(`- Documents unchanged: ${totalProcessed - totalUpdated}`);
  } catch (error) {
    console.error("Error during migration:", error);
    throw new Error(
      `Migration failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};

// 타입 가드 함수 추가
export const isValidModelName = (modelName: any): boolean => {
  return typeof modelName === "string" && modelName.trim().length > 0;
};

export const updateUserCollectionWithCredit = async (): Promise<void> => {
  const BATCH_SIZE = 500;
  let totalProcessed = 0;
  let totalUpdated = 0;
  let lastDoc: DocumentSnapshot | null = null;

  try {
    console.log("Starting user collection update with credit field...");

    do {
      // 쿼리 구성
      let q = query(collection(db, Collections.USER), limit(BATCH_SIZE));
      if (lastDoc) {
        q = query(
          collection(db, Collections.USER),
          startAfter(lastDoc),
          limit(BATCH_SIZE)
        );
      }

      const snapshot = await getDocs(q);
      if (snapshot.empty) break;

      const batch = writeBatch(db);

      for (const userDoc of snapshot.docs) {
        const userData = userDoc.data() as User;
        totalProcessed++;

        // credit 필드가 없는 경우에만 업데이트
        if (!userData.hasOwnProperty("credit")) {
          // songRequestList에서 COMPLETE 상태인 요청이 있는지 확인
          const hasCompletedRequest = userData.songRequestList?.some(
            (request) => request.status === Status.COMPLETE
          );

          batch.update(userDoc.ref, {
            credit: {
              balance: hasCompletedRequest ? 0 : 1,
              referralCode: generateRandomString(6),
            },
          });

          totalUpdated++;
        }
      }

      // 배치 커밋
      if (totalUpdated > 0) {
        await batch.commit();
        console.log(
          `Committed batch: ${totalUpdated}/${totalProcessed} documents processed`
        );
      }

      lastDoc = snapshot.docs[snapshot.docs.length - 1];
    } while (lastDoc);

    console.log("Credit field update completed successfully:");
    console.log(`- Total documents processed: ${totalProcessed}`);
    console.log(`- Total documents updated: ${totalUpdated}`);
    console.log(`- Documents unchanged: ${totalProcessed - totalUpdated}`);
  } catch (error) {
    console.error("Error during credit field update:", error);
    throw new Error(
      `Credit field update failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};

export const migrateSongRequestStatus = async (): Promise<void> => {
  const BATCH_SIZE = 500;
  let totalProcessed = 0;
  let totalUpdated = 0;
  let affectedUsers = new Set<string>();

  try {
    console.log("Starting status migration process...");

    // 'COMPLETE' 문자열을 직접 사용하여 쿼리
    const songRequestQuery = query(
      collection(db, Collections.SONG_REQUEST),
      // @ts-ignore - Intentionally querying for incorrect status value
      where("status", "==", "COMPLETE")
    );

    const songRequestSnapshot = await getDocs(songRequestQuery);
    const totalDocuments = songRequestSnapshot.size;

    if (totalDocuments === 0) {
      console.log("No documents found with incorrect status.");
      return;
    }

    console.log(`Found ${totalDocuments} documents with incorrect status.`);

    // Process in batches
    let batch = writeBatch(db);
    let batchCount = 0;

    // First pass: Update SongRequest documents and collect affected user IDs
    for (const songRequestDoc of songRequestSnapshot.docs) {
      const songRequest = songRequestDoc.data() as SongRequest;
      totalProcessed++;

      // Add to batch update
      batch.update(songRequestDoc.ref, {
        status: Status.COMPLETE, // 올바른 enum 값 사용
      });

      // Collect affected users
      affectedUsers.add(songRequest.userId);
      songRequest.otherUserList?.forEach((user) => {
        affectedUsers.add(user.userId);
      });

      batchCount++;
      totalUpdated++;

      // Commit batch if size limit reached
      if (batchCount >= BATCH_SIZE) {
        await batch.commit();
        console.log(
          `Committed batch: ${totalProcessed}/${totalDocuments} SongRequests processed`
        );
        batch = writeBatch(db);
        batchCount = 0;
      }
    }

    // Commit any remaining SongRequest updates
    if (batchCount > 0) {
      await batch.commit();
      console.log(`Committed final batch of ${batchCount} SongRequest updates`);
    }

    console.log(`Found ${affectedUsers.size} affected users to update`);

    // Second pass: Update User documents
    for (const userId of affectedUsers) {
      try {
        await runTransaction(db, async (transaction) => {
          const userRef = doc(db, Collections.USER, userId);
          const userDoc = await transaction.get(userRef);

          if (!userDoc.exists()) {
            console.log(`User ${userId} not found, skipping`);
            return;
          }

          const userData = userDoc.data() as User;
          let needsUpdate = false;

          // Update songRequestList status if needed
          const updatedSongRequestList = userData.songRequestList.map(
            (request) => {
              // @ts-ignore - Intentionally checking for incorrect status value
              if (request.status === "COMPLETE") {
                needsUpdate = true;
                return {
                  ...request,
                  status: Status.COMPLETE,
                };
              }
              return request;
            }
          );

          if (needsUpdate) {
            transaction.update(userRef, {
              songRequestList: updatedSongRequestList,
            });
            console.log(`Updated user ${userId}'s songRequestList`);
          }
        });
      } catch (error) {
        console.error(`Error updating user ${userId}:`, error);
      }
    }

    console.log("\nMigration Summary:");
    console.log(`- Total SongRequests processed: ${totalProcessed}`);
    console.log(`- Total SongRequests updated: ${totalUpdated}`);
    console.log(`- Total Users processed: ${affectedUsers.size}`);
  } catch (error) {
    console.error("Error during migration:", error);
    throw new Error(
      `Migration failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};
